import { FlexColumn, Loading, useLoading } from '@gorila-shared-ui/components';
import { Block } from 'baseui/block';
import { useEffect, useState } from 'react';
import { MEDIA_SAFE_GPT_ORIGIN } from '../../constants/media';
import { useStyles } from '../../hooks/useStyles';
import { getS3FileUrlByKey } from '../../services/appService';
import { MediaCore } from '../../types/media';
import { Notification, NotificationLinkableSection } from '../../types/notification';
import { NotificationOrigin } from '../../types/notificationConfig';
import MediaPreviewModal from '../shared/MediaPreviewModal';
import MediaThumbnail from '../shared/MediaThumbnail';

type Props = Readonly<{
  notification: Notification;
}>;

function NotificationEvidences({ notification }: Props) {
  const { theme, css } = useStyles();
  const { loading, startLoading, stopLoading } = useLoading();
  const [evidenceFiles, setEvidenceFiles] = useState<MediaCore[]>();
  const [selectedEvidenceIndex, setSelectedEvidenceIndex] = useState<number>();
  const details = notification?.actions?.viewDetails;
  const section = details?.section;

  const styles = css({
    borderRadius: theme.borders.popoverBorderRadius,
    width: '100%',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    minHeight: 'fit-content',
  });

  useEffect(() => {
    if (section !== NotificationLinkableSection.alarmEvidences || !notification.origin) return;

    const loadEvidencesUrl = async () => {
      if (details?.params.evidences.length) {
        const evidences = details.params.evidences;
        startLoading();
        const newEvidences = [...evidences];
        const requests = evidences.map((evidence, index) => {
          return getS3FileUrlByKey(
            evidence.key,
            [NotificationOrigin.safeGpt, MEDIA_SAFE_GPT_ORIGIN].includes(notification.origin!),
          ).then((result) => ({ ...result, index }));
        });
        const results = await Promise.allSettled(requests);
        results.forEach((result) => {
          if (result.status === 'fulfilled') {
            const index = result.value.index;
            const url = result.value.url ?? '';
            const evidence = newEvidences[index];
            newEvidences[index] = {
              ...evidence,
              url,
            };
          }
        });
        setEvidenceFiles(newEvidences);
        stopLoading();
      }
    };

    loadEvidencesUrl();
  }, [details?.params, section]);

  return (
    <>
      <FlexColumn
        gap={theme.sizing.scale400}
        classNames={css({ flex: 1, overflow: 'auto' })}
      >
        {loading && <Loading />}

        {!loading &&
          evidenceFiles?.map((media, mediaIndex) => (
            <Block
              className={styles}
              key={mediaIndex}
            >
              <MediaThumbnail
                mediaKey={media.key}
                origin={notification.origin!}
                minHeight={150}
                onPreview={() => {
                  setSelectedEvidenceIndex(mediaIndex);
                }}
              />
            </Block>
          ))}
      </FlexColumn>
      {selectedEvidenceIndex !== undefined && evidenceFiles && (
        <MediaPreviewModal
          isOpen
          onClose={() => setSelectedEvidenceIndex(undefined)}
          mediaFiles={evidenceFiles}
          selectedItemIndex={selectedEvidenceIndex}
        />
      )}
    </>
  );
}

export default NotificationEvidences;
